import React from 'react';
import { NavLink, useNavigate, Outlet } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useClub } from '@/contexts/ClubContext';
import { useQuery } from '@tanstack/react-query';
import {
  LayoutDashboard,
  Users,
  Building2,
  ListOrdered,
  Trophy,
  History,
  Settings,
  LogOut,
  User,
  Target,
  Wrench,
  Grid2X2
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { buildAvatarDataUri } from '@/lib/avatarBuilder';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useEffect } from 'react';
import { cn } from '@/lib/utils';
import { billingService, clubService, memberService } from '@/services/storageService';
import apiClient, { API_BASE as API_BASE_URL } from '@/services/apiClient';
import { applyClubTheme } from '@/lib/theme';
import aivLogo from '@/assets/aiv-logo-trans.png';
import placeholderImage from '@/assets/aiv-logo-trans.png';
import VerticalAppLayout from './VerticalAppLayout';
import { useI18n } from '@/contexts/I18nContext';

export default function DashboardLayout() {
  const { t } = useI18n();
  const { user, logout } = useAuth();
  const {
    activeClubId,
    selectClub,
    refreshData,
    members,
    currentClubRole,
    club: activeClub,
    hasLoadedInitial,
    isLoading: clubLoading,
    isRefreshing,
  } = useClub();
  const navigate = useNavigate();
  const clubId = activeClubId ? Number(activeClubId) : null;
  const hasClub = Boolean(clubId);

  const clubQuery = useQuery({
    queryKey: ['club', clubId],
    queryFn: () => clubService.get(clubId as number),
    enabled: Boolean(clubId),
  });
  const clubRoleQuery = useQuery({
    queryKey: ['club-role', clubId, user?.id, user?.email],
    queryFn: async () => {
      if (!clubId || !user) return null;
      const list = (await memberService.list(clubId as number)) as any[];
      const match = list.find(
        (m) =>
          String(m.member_id) === String(user.id) ||
          (m.email && user.email && String(m.email).toLowerCase() === String(user.email).toLowerCase()),
      );
      return match?.role ? String(match.role).toLowerCase() : null;
    },
    enabled: Boolean(clubId && user),
    staleTime: 60_000,
  });

  const club = clubQuery.data as any;
  const clubsQuery = useQuery({
    // include user id in the key so clubs cache resets per user
    queryKey: ['my-clubs', user?.id],
    queryFn: () => clubService.listMine(),
    enabled: Boolean(user),
    staleTime: 60_000,
  });
  const profileQuery = useQuery({
    queryKey: ['profile'],
    queryFn: () => apiClient.request('/profile/'),
    enabled: Boolean(user),
    staleTime: 60_000,
  });
  const clubs = Array.isArray(clubsQuery.data) ? clubsQuery.data : [];
  const billingQuery = useQuery({
    queryKey: ['billing-status', clubId],
    queryFn: () => billingService.getStatus(clubId),
    enabled: Boolean(user && clubId),
    staleTime: 60_000,
  });
  const normalizedPackageCode = String(
    (billingQuery.data as any)?.package ||
      (billingQuery.data as any)?.recommended_package ||
      'package1',
  )
    .trim()
    .toLowerCase();
  const isElitePackage = normalizedPackageCode === 'package3';
  const themeQuery = useQuery({
    queryKey: ['club-theme', clubId],
    queryFn: () => clubService.getTheme(clubId as number),
    enabled: Boolean(clubId),
    staleTime: 15_000,
    retry: false,
    refetchOnWindowFocus: false,
  });
  const membership = user
    ? members.find(
      (m) =>
        m.id === String(user.id) ||
        m.userId === String(user.id) ||
        (m.email && user.email && m.email.toLowerCase() === user.email.toLowerCase()),
    )
    : undefined;
  const normalizeLogoUrl = (raw?: string | null) => {
    if (!raw) return '';
    const trimmed = String(raw).trim();
    if (!trimmed) return '';
    const rebuild = (path: string) => {
      if (!API_BASE_URL) return path;
      return `${API_BASE_URL}${path.startsWith('/') ? '' : '/'}${path}`;
    };
    try {
      const parsed = new URL(trimmed);
      const pathWithSearch = `${parsed.pathname}${parsed.search}${parsed.hash}`;
      return rebuild(pathWithSearch);
    } catch {
      return rebuild(trimmed);
    }
  };
  const logoUrl = isElitePackage ? normalizeLogoUrl(club?.logo_url || club?.logo) : '';

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const navItems = [
    { to: '/dashboard', icon: LayoutDashboard, label: t('nav_dashboard', 'Dashboard'), roles: ['admin', 'coach', 'member'], requiresClub: false },
    { to: '/dashboard/club', icon: Building2, label: t('nav_club', 'Club'), roles: ['admin'], requiresClub: false },
    { to: '/dashboard/leaderboard', icon: Trophy, label: t('nav_leaderboard', 'Leaderboard'), roles: ['admin', 'coach', 'member'], requiresClub: true },
    { to: '/dashboard/members', icon: Users, label: t('nav_members', 'Members'), roles: ['admin'], requiresClub: true },
    { to: '/dashboard/queue', icon: ListOrdered, label: t('nav_queue_courts', 'Queue & Courts'), roles: ['admin', 'coach', 'member'], requiresClub: true },
    { to: '/dashboard/evaluations', icon: Target, label: t('nav_evaluations', 'Evaluations'), roles: ['admin', 'coach'], requiresClub: true },
    { to: '/dashboard/equipment', icon: Wrench, label: t('nav_equipment', 'Equipment'), roles: ['admin', 'member', 'coach'], requiresClub: true },
    { to: '/dashboard/history', icon: History, label: t('nav_match_history', 'Match History'), roles: ['admin', 'coach', 'member'], requiresClub: true },
    { to: '/dashboard/daily-report', icon: Grid2X2, label: t('nav_daily_report', 'Daily Match Report'), roles: ['admin', 'coach'], requiresClub: true },
    { to: '/dashboard/profile', icon: User, label: t('nav_profile', 'Profile'), roles: ['admin', 'coach', 'member'], requiresClub: true },
    { to: '/dashboard/settings', icon: Settings, label: t('nav_theme_app', 'Theme & App'), roles: ['admin'], requiresClub: true },
  ];

  const membershipRole = user
    ? membership?.role
    : undefined;
  const clubRoleFromQuery = clubRoleQuery.data || null;
  const roleForNavRaw = clubRoleFromQuery || currentClubRole || membershipRole || user?.role;
  const roleForNav = roleForNavRaw === 'player' ? 'member' : roleForNavRaw;
  const profileAvatar = (profileQuery.data as any)?.avatar_selection;
  const avatarUrl =
    (profileAvatar && buildAvatarDataUri(profileAvatar.collection_name, profileAvatar.avatar_seed)) ||
    membership?.avatarUrl ||
    buildAvatarDataUri(membership?.avatarCollection, membership?.avatarSeed);
  const avatarAlt =
    profileAvatar?.avatar_name ||
    membership?.avatarName ||
    `${membership?.firstName || user?.firstName || ''} ${membership?.lastName || user?.lastName || ''}`.trim() ||
    user?.email ||
    'User avatar';
  const getInitials = () => {
    const first = profileAvatar?.avatar_name?.split(' ')?.[0] || membership?.firstName || user?.firstName || '';
    const last = profileAvatar?.avatar_name?.split(' ')?.[1] || membership?.lastName || user?.lastName || '';
    const initials = `${first?.[0] || ''}${last?.[0] || ''}`.trim();
    if (initials) return initials.toUpperCase();
    return (user?.email?.[0] || '').toUpperCase() || '?';
  };
  const roleLabel = (() => {
    const raw = (roleForNavRaw || '').toString().toLowerCase();
    if (raw === 'admin') return t('role_admin', 'Admin');
    if (raw === 'coach') return t('role_coach', 'Coach');
    if (raw === 'member' || raw === 'player') return t('role_player', 'Player');
    return '';
  })();

  const filteredNavItems = navItems.filter(item =>
    user && roleForNav && item.roles.includes(roleForNav) && (hasClub || item.requiresClub === false)
  );

  const themeSettings = themeQuery.data || null;

  useEffect(() => {
    if (!clubId) {
      applyClubTheme(null);
      return;
    }
    applyClubTheme(themeSettings || null);
  }, [clubId, themeSettings]);

  useEffect(() => {
    if (!activeClubId && clubs.length > 0) {
      selectClub(Number(clubs[0].id));
    }
  }, [activeClubId, clubs, selectClub]);

  useEffect(() => {
    const hasSelection = Boolean(activeClubId || activeClub);
    if (!hasSelection && hasLoadedInitial && !clubLoading && !isRefreshing) {
      navigate('/select-club', { replace: true });
    }
  }, [activeClubId, club, hasLoadedInitial, clubLoading, isRefreshing, navigate]);

  return (
    <VerticalAppLayout
      mobileTitle={club?.name || 'Dashboard'}
      sidebarStyle={{
        backgroundColor: themeSettings?.menu_bg_color || undefined,
        fontFamily: themeSettings?.menu_font || undefined,
      }}
      sidebarHeader={(
        <div className="p-6">
          <div className="flex flex-col items-center text-center gap-2 w-full">
            <div className="rounded-full flex items-center justify-center overflow-hidden flex-shrink-0 w-24 h-24">
              <img
                src={logoUrl || placeholderImage}
                alt={club?.name || 'Club logo'}
                className="w-full h-full object-cover"
                onError={(e) => {
                  if ((e.currentTarget as HTMLImageElement).dataset.fallbackApplied === 'true') return;
                  (e.currentTarget as HTMLImageElement).dataset.fallbackApplied = 'true';
                  e.currentTarget.src = placeholderImage;
                }}
              />
            </div>

            {clubs.length > 0 && (
              <Select
                value={activeClubId ? String(activeClubId) : undefined}
                onValueChange={async (value) => {
                  const nextId = Number(value);
                  await selectClub(nextId);
                  await refreshData(nextId);
                }}
              >
                <SelectTrigger className="h-8 w-full bg-sidebar-accent text-sidebar-accent-foreground border-sidebar-border">
                  <SelectValue placeholder={t('ui_select_club', 'Select club')} />
                </SelectTrigger>
                <SelectContent>
                  {clubs.map((c: any) => (
                    <SelectItem key={c.id} value={String(c.id)}>
                      {c.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>
        </div>
      )}
      sidebarNav={(
        <>
          {filteredNavItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === '/dashboard'}
              className={({ isActive }) => cn('nav-link', isActive && 'active')}
            >
              <item.icon size={20} />
              <span>{item.label}</span>
            </NavLink>
          ))}
        </>
      )}
      sidebarFooter={(
        <div className="space-y-3">
          <div className="flex items-start gap-3">
            <Avatar className="mt-0.5">
              {avatarUrl && <AvatarImage src={avatarUrl} alt={avatarAlt} />}
              <AvatarFallback className="bg-sidebar-accent text-sidebar-foreground">
                {getInitials()}
              </AvatarFallback>
            </Avatar>
            <div className="min-w-0">
              <p className="text-xs font-semibold text-sidebar-foreground/70 capitalize">
                {roleLabel}
              </p>
              <p className="text-sm font-medium text-sidebar-foreground truncate">{user?.email}</p>
            </div>
          </div>
          <Button
            variant="ghost"
            onClick={handleLogout}
            className="w-full justify-start text-sidebar-foreground/70 hover:text-sidebar-foreground hover:bg-sidebar-accent"
          >
            <LogOut size={20} className="mr-3" />
            {t('ui_logout', 'Logout')}
          </Button>
          <div className="text-center text-xs text-sidebar-foreground/60">
            <div className="flex justify-center mb-2">
              <img
                src={aivLogo}
                style={{ height: '60px', width: '60px' }}
                alt="Aiv Studios"
                className="object-contain"
              />
            </div>
            {t('footer_developed_by', 'Developed by')}{' '}
            <a
              href="https://aivstudios.com/"
              target="_blank"
              rel="noreferrer"
              className="underline"
            >
              Aiv Studios
            </a>
            .
            <br />
            {t('footer_rights', 'All rights reserved © 2026.')}
          </div>
        </div>
      )}
    >
      <div className="flex-1 min-h-screen min-w-0">
        <div className="p-4 lg:p-8">
          <Outlet />
        </div>
      </div>
    </VerticalAppLayout>
  );
}
